/**
 * Full-page Activity Form view (replaces the small modal).
 * Used when sidebar navigation switches to "activities" view.
 * Single source of truth for the form fields — also used in modal Edit mode.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, FileSignature, RefreshCw,
} from "lucide-react";
import { API } from "../../lib/api";
import { ENTRY_INITIAL, ENTRY_OPTS } from "../../lib/dashConstants";

const REQUIRED = [
  "nama_sales","status_sekolah","npsn","alamat","tujuan_sales","kegiatan",
  "dilakukan_via","bertemu_dengan","jabatan","tanggal_visit","produk_menarik",
  "demo_speakeng","respon_sekolah","detail_issue",
];

const SECTIONS = [
  { title: "Informasi Sales & Status", fields: [
    ["nama_sales","Nama Sales","select"],
    ["status_sekolah","Status Sekolah","select"],
    ["tujuan_sales","Tujuan Sales","select"],
    ["tanggal_visit","Tanggal & Jam Visit","datetime-local"],
  ]},
  { title: "Informasi Sekolah", fields: [
    ["nama_sekolah","Nama Sekolah","text"],
    ["npsn","NPSN","text"],
    ["nama_yayasan","Nama Yayasan","text"],
    ["alamat","Alamat","text"],
  ]},
  { title: "Detail Kegiatan", fields: [
    ["kegiatan","Kegiatan","select"],
    ["kegiatan_lainnya","Deskripsi Kegiatan Lainnya","text"],
    ["dilakukan_via","Dilakukan Via","select"],
    ["bertemu_dengan","Bertemu Dengan","text"],
    ["jabatan","Jabatan","select"],
    ["jabatan_lainnya","Jabatan Lainnya","text"],
  ]},
  { title: "Produk & Demo", fields: [
    ["produk_menarik","Produk Menarik","select"],
    ["demo_speakeng","DEMO SpeakEng?","select"],
    ["respon_demo","Alasan / Respon SpeakEng","text"],
  ]},
  { title: "Respon & Issue", fields: [
    ["respon_sekolah","Bagaimana Respon?","text"],
    ["detail_issue","Detail Issue","text"],
  ]},
];

export default function ActivityFormView({
  mode = "create",      // "create" | "edit"
  initialData = null,    // when edit, must include row_index
  onSuccess,             // (newRow) => void
  onCancel,              // () => void (used to switch view back)
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(ENTRY_INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit && initialData) {
      const dt = initialData.tanggal_visit
        ? new Date(initialData.tanggal_visit).toISOString().slice(0, 16)
        : "";
      setForm({
        ...ENTRY_INITIAL,
        ...Object.fromEntries(
          Object.keys(ENTRY_INITIAL).map((k) => [k, initialData[k] ?? ""])
        ),
        tanggal_visit: dt,
      });
    } else {
      setForm(ENTRY_INITIAL);
    }
    setError(null);
    setConfirmOpen(false);
  }, [isEdit, initialData]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupNPSN = async () => {
    const npsn = (form.npsn || "").trim();
    if (!npsn) { setError("Isi NPSN dulu untuk auto-lookup."); window.scrollTo({top: 0, behavior: "smooth"}); return; }
    try {
      const r = await axios.get(`${API}/schools/lookup/${encodeURIComponent(npsn)}`);
      if (r.data?.ok) {
        setForm((f) => ({ ...f, nama_sekolah: r.data.nama_sekolah,
          alamat: f.alamat || r.data.alamat || "" }));
        setError(null);
      } else {
        setError(r.data?.reason || "NPSN tidak ditemukan di database kemendikdasmen");
        window.scrollTo({top: 0, behavior: "smooth"});
      }
    } catch (e) { setError("Gagal lookup NPSN — coba lagi"); }
  };

  const validate = () => {
    const missing = REQUIRED.filter((k) => !form[k]);
    if (missing.length) {
      setError(`Field wajib kosong: ${missing.join(", ")}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    if (!/^\d{3,15}$/.test(String(form.npsn).trim())) {
      setError("NPSN harus angka 3-15 digit");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }
    setError(null);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/sales/activities`, {
          row_index: initialData.row_index,
          payload: form,
        });
      } else {
        await axios.post(`${API}/sales/activities`, form);
      }
      onSuccess && onSuccess(form);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal menyimpan");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-page" data-testid="activity-form-page">
      <div className="form-page-head">
        <button className="btn btn-ghost" onClick={onCancel} data-testid="form-back">
          <ArrowLeft size={14}/> Kembali
        </button>
        <div style={{flex: 1}}>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>
            <FileSignature size={20} style={{verticalAlign: "-3px", marginRight: 8}}/>
            {isEdit ? "Edit Aktivitas Sales" : "Tambah Aktivitas Sales Baru"}
          </h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            {isEdit
              ? `Mengubah baris #${initialData?.row_index}. Data akan tertimpa & menjadi versi terbaru.`
              : "Setiap submit otomatis menjadi data terbaru — sistem cek anti-duplikasi (Sales + NPSN + Tanggal)."}
          </p>
        </div>
      </div>

      {error && (
        <div className="alert err" data-testid="form-error" style={{margin: "16px 0"}}>
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      <div className="form-sections">
        {SECTIONS.map((sec) => (
          <section key={sec.title} className="form-section">
            <h3 className="form-section-title">{sec.title}</h3>
            <div className="form-grid">
              {sec.fields.map(([key, label, type]) => {
                const required = REQUIRED.includes(key);
                return (
                  <div key={key} className="form-field">
                    <label>
                      {label}
                      {required && <span className="req">*</span>}
                      {key === "npsn" && (
                        <button type="button"
                          style={{marginLeft: 10, padding: "2px 10px", fontSize: 11, borderRadius: 12,
                                  border: "1px solid var(--accent)", background: "rgba(46,134,222,0.08)",
                                  color: "var(--accent)", cursor: "pointer", fontWeight: 600}}
                          onClick={lookupNPSN} data-testid="lookup-npsn-btn"
                          title="Auto-fill Nama Sekolah dari NPSN (data kemendikdasmen)">
                          🔍 Auto-fill via NPSN
                        </button>
                      )}
                    </label>
                    {type === "select" ? (
                      <select
                        className="form-input"
                        value={form[key]}
                        onChange={(e) => upd(key, e.target.value)}
                        data-testid={`entry-${key}`}
                      >
                        <option value="">— Pilih —</option>
                        {(ENTRY_OPTS[key] || []).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="form-input"
                        type={type}
                        value={form[key]}
                        onChange={(e) => upd(key, e.target.value)}
                        maxLength={500}
                        data-testid={`entry-${key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={submitting}
                data-testid="form-cancel">
          Batal
        </button>
        <button
          className="btn btn-primary"
          onClick={() => validate() && setConfirmOpen(true)}
          disabled={submitting}
          data-testid="entry-submit"
        >
          <CheckCircle2 size={14}/> {isEdit ? "Simpan Perubahan" : "Submit Aktivitas"}
        </button>
      </div>

      {confirmOpen && (
        <div className="modal-backdrop" style={{zIndex: 200}} onClick={() => setConfirmOpen(false)}>
          <div className="modal" style={{maxWidth: 480}} onClick={(e) => e.stopPropagation()}>
            <h3 style={{margin: 0, color: "var(--navy)"}}>
              {isEdit ? "Konfirmasi Perubahan" : "Konfirmasi Data Baru"}
            </h3>
            <p style={{fontSize: 13, color: "var(--text)", marginTop: 10}}>
              {isEdit
                ? "Data lama akan ditimpa. Versi terbaru menjadi sumber kebenaran (semua user lain otomatis melihat versi ini)."
                : "Sistem akan cek duplikasi (Sales + Tanggal + NPSN). Setelah submit, data menjadi yang terbaru dan tidak bisa dibatalkan ke versi sebelumnya."}
            </p>
            <ul style={{fontSize: 12, color: "var(--muted)", marginTop: 8}}>
              <li>Sales: <b>{form.nama_sales}</b></li>
              <li>NPSN: <b>{form.npsn}</b> · Sekolah: <b>{form.nama_sekolah || "(kosong)"}</b></li>
              <li>Tanggal: <b>{form.tanggal_visit}</b></li>
              <li>Tujuan: <b>{form.tujuan_sales}</b> · Kegiatan: <b>{form.kegiatan}</b></li>
            </ul>
            <div style={{display: "flex", gap: 10, marginTop: 16}}>
              <button className="btn btn-secondary" onClick={() => setConfirmOpen(false)} style={{flex: 1}}>
                Batal
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={submitting}
                style={{flex: 1}}
                data-testid="entry-confirm"
              >
                {submitting
                  ? <><RefreshCw size={14} className="spin"/> Memproses...</>
                  : (isEdit ? "Ya, Simpan" : "Ya, Submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
